export const QUESTIONNAIRE_STATUSES = { active: 'active', archived: 'archived' } as const;
export type QuestionnaireStatus =
  (typeof QUESTIONNAIRE_STATUSES)[keyof typeof QUESTIONNAIRE_STATUSES];

/** `all` is a list filter only; a questionnaire is never stored with it. */
export const QUESTIONNAIRE_STATUS_FILTERS = { ...QUESTIONNAIRE_STATUSES, all: 'all' } as const;
export type QuestionnaireStatusFilter =
  (typeof QUESTIONNAIRE_STATUS_FILTERS)[keyof typeof QUESTIONNAIRE_STATUS_FILTERS];

export const QUESTIONNAIRE_VERSION_STATUSES = {
  draft: 'draft',
  published: 'published',
  retired: 'retired',
} as const;
export type QuestionnaireVersionStatus =
  (typeof QUESTIONNAIRE_VERSION_STATUSES)[keyof typeof QUESTIONNAIRE_VERSION_STATUSES];

export const QUESTION_KINDS = {
  singleChoice: 'single_choice',
  multipleChoice: 'multiple_choice',
  trueFalse: 'true_false',
  shortText: 'short_text',
} as const;
export type QuestionKind = (typeof QUESTION_KINDS)[keyof typeof QUESTION_KINDS];

/** The two options a `true_false` question always has, in this order. */
export const TRUE_FALSE_OPTION_TEXTS = ['True', 'False'] as const;

export const QUESTIONNAIRE_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,39}$/;

export const QUESTIONNAIRE_LIMITS = {
  maxQuestions: 200,
  maxOptions: 12,
  maxPoints: 1000,
  maxPromptLength: 4000,
  maxOptionLength: 1000,
  maxModelAnswerLength: 4000,
  maxTitleLength: 200,
  maxDescriptionLength: 4000,
  maxTotalMarks: 10000,
} as const;

export interface QuestionOption {
  id: string;
  position: number;
  text: string;
  isCorrect: boolean;
}

export interface Question {
  id: string;
  position: number;
  kind: QuestionKind;
  prompt: string;
  points: number;
  required: boolean;
  /** Only a `short_text` question has one; it is guidance for the grader. */
  modelAnswer: string | null;
  /** Empty for `short_text`. */
  options: QuestionOption[];
}

export interface QuestionnaireVersionSummary {
  id: string;
  number: number;
  status: QuestionnaireVersionStatus;
  passMarkPercent: number;
  /** `null` means no time limit. */
  timeLimitMinutes: number | null;
  /** `null` means unlimited attempts. */
  maxAttempts: number | null;
  /** `null` means a certificate never expires. */
  certificateValidityMonths: number | null;
  issuesCertificate: boolean;
  /** `null` means every question is asked; otherwise each attempt draws this many at random. */
  questionsPerAttempt: number | null;
  /** Random question order, even when every question is asked. */
  shuffleQuestions: boolean;
  /** Random option order for single and multiple choice questions. True/false stays True, False. */
  shuffleOptions: boolean;
  /** `null` means a score is shown as the points as written; otherwise it is scaled to this. */
  totalMarks: number | null;
  questionCount: number;
  totalPoints: number;
  publishedAt: string | null;
  publishedById: string | null;
  retiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What stops a version being published. */
export interface VersionProblem {
  /** 1-based position of the question at fault; `null` for a problem with the version itself. */
  position: number | null;
  message: string;
}

export interface QuestionnaireVersionDetail extends QuestionnaireVersionSummary {
  questionnaireId: string;
  questions: Question[];
  /** What publishing would refuse for. Always empty once a version is published or retired. */
  problems: VersionProblem[];
}

export interface QuestionnaireSummary {
  id: string;
  code: string;
  title: string;
  description: string;
  status: QuestionnaireStatus;
  versionCount: number;
  /** Highest version number, whatever its status. */
  latestVersionNumber: number;
  /** `null` when no version is published. */
  publishedVersionNumber: number | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface QuestionnaireDetail extends QuestionnaireSummary {
  /** Newest first. */
  versions: QuestionnaireVersionSummary[];
}

export interface ListQuestionnairesQuery {
  search?: string;
  /** Defaults to `active`. */
  status?: QuestionnaireStatusFilter;
}

export interface CreateQuestionnaireInput {
  code: string;
  title: string;
  description?: string;
}

export interface CreateQuestionnairePayload {
  input: CreateQuestionnaireInput;
  actorId: string;
}

/** The code is not here: it is fixed at creation, which keeps the test-record mark fixed too. */
export interface UpdateQuestionnaireInput {
  title?: string;
  description?: string;
}

export interface UpdateQuestionnairePayload {
  id: string;
  input: UpdateQuestionnaireInput;
  actorId: string;
}

export interface QuestionnaireActionPayload {
  id: string;
  actorId: string;
}

export interface QuestionnaireVersionPayload {
  id: string;
  number: number;
  actorId: string;
}

export interface QuestionOptionInput {
  text: string;
  isCorrect: boolean;
}

export interface QuestionInput {
  kind: QuestionKind;
  prompt: string;
  points: number;
  required: boolean;
  modelAnswer?: string | null;
  /**
   * Refused for `short_text`. For `true_false` the service writes the two texts
   * itself and reads only which of the first two is correct.
   */
  options: QuestionOptionInput[];
}

/** Replaces the whole draft: its settings and every question. */
export interface SaveDraftInput {
  passMarkPercent: number;
  timeLimitMinutes: number | null;
  maxAttempts: number | null;
  certificateValidityMonths: number | null;
  issuesCertificate: boolean;
  /** Omitted or `null`: every question is asked. */
  questionsPerAttempt?: number | null;
  /** Omitted: false. */
  shuffleQuestions?: boolean;
  /** Omitted: false. */
  shuffleOptions?: boolean;
  /** Omitted or `null`: points as written. */
  totalMarks?: number | null;
  questions: QuestionInput[];
}

export interface SaveDraftPayload {
  id: string;
  number: number;
  input: SaveDraftInput;
  actorId: string;
}

export interface PurgeTestQuestionnairesResult {
  questionnairesDeleted: number;
  codesPurged: string[];
}

export const QUESTIONNAIRE_ERROR_CODES = {
  codeExists: 'QUESTIONNAIRE_CODE_EXISTS',
  archived: 'QUESTIONNAIRE_ARCHIVED',
  versionNotEditable: 'QUESTIONNAIRE_VERSION_NOT_EDITABLE',
  versionInvalid: 'QUESTIONNAIRE_VERSION_INVALID',
  versionNotPublished: 'QUESTIONNAIRE_VERSION_NOT_PUBLISHED',
} as const;
